import React from "react";
import { Box } from "@mui/material";
import { tokens } from "../theme";

/**
 * An inline message: something happened, said in place.
 *
 * The book's rule is that a modal means a decision cannot be deferred, and
 * everything else — confirmations, results, warnings — happens inline so nobody
 * loses the row they were working on.
 *
 * Its look is deliberately quiet: a tinted ground, no border, and no icon
 * glyph. A dot is enough to carry the tone, and it sits at `margin-top: 6px`
 * so it aligns with the first line of text rather than the centre of the block.
 *
 * Write it as two sentences. `title` states what happened; the children say
 * what it means — "Two bank details are missing." / "Those people will not be
 * paid this cycle." A message that only states the fact leaves the reader to
 * work out the consequence, which is the part they actually need.
 */
const TONES = {
  success: { bg: tokens.successTint, text: tokens.msgSuccessText, dot: tokens.successText },
  warning: { bg: tokens.warningTint, text: tokens.msgWarningText, dot: tokens.warningText },
  danger: { bg: tokens.dangerTint, text: tokens.msgDangerText, dot: tokens.danger },
  info: { bg: tokens.infoTint, text: tokens.msgInfoText, dot: tokens.infoText },
  pending: { bg: tokens.pendingTint, text: tokens.pendingText, dot: tokens.pending },
};

const InlineMessage = ({ tone = "info", title, children, sx, ...rest }) => {
  const t = TONES[tone] || TONES.info;
  return (
    <Box
      // Announced without stealing focus: a warning a user should hear about
      // when it appears, but not one that interrupts what they are typing.
      role="status"
      {...rest}
      sx={{
        display: "flex",
        gap: "12px",
        background: t.bg,
        borderRadius: "8px",
        padding: "14px 16px",
        fontSize: "0.875rem",
        lineHeight: 1.5,
        color: t.text,
        ...sx,
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          background: t.dot,
          flex: "none",
          marginTop: "6px",
        }}
      />
      <Box>
        {title && <strong style={{ fontWeight: 600 }}>{title}</strong>}
        {title && children ? " " : null}
        {children}
      </Box>
    </Box>
  );
};

export default InlineMessage;
